import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import { enableMapSet } from "immer";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { flowRight, template } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";
import { TypeAttributes } from "rsuite/esm/internals/types";

import {
    Container,
    Content,
    Footer as RsuiteFooter,
    Header,
    Heading,
    Message,
    toaster,
    Text,
    Button,
    Schema,
} from "rsuite";

import constants from "utils/constants";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import {
    getTenantCustomerType,
    getTenantId,
} from "store/environments/tenants/tenants-selectors";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";
import {
    CustomField,
    CustomFieldType,
    ExcludedLineType,
    InvoiceType,
    Separator,
    TtpMessageSourceField,
} from "store/environments/tenants/custom-fields/erp-types";

import { fetchEnvironments } from "store/environments/environment-thunks";
import { fetchTenants } from "store/environments/tenants/tenants-thunks";

import { getErrorMessage } from "api/defaults";
import {
    loadCustomFields as loadErpCustomFields,
    loadLineTypes,
    loadSeparators,
    loadTargetFields,
    loadTargetTables,
    loadTtpMessageSourceFields,
    loadTypes,
    removeCustomFields,
    updateCustomFields,
} from "api/environments/tenants/custom-fields/erp";

import { loadCustomFields as loadDooapCustomFields } from "api/environments/tenants/custom-fields/dooap";

import useRequestCancellation from "hooks/useRequestCancellation";
import useRefreshPage from "hooks/useRefreshPage";

import withLoader from "hocs/withLoader/withLoader";
import withError from "hocs/withError/withError";

import {
    checkCustomFieldsInitialized,
    getErpType,
    getInvoiceKeyPrefix,
    getTableKeyPrefix,
} from "../custom-fields-helpers";

import Table, { TableItem } from "./Table/Table";
import { buildUrl } from "../../sidbar-helpers";

import Footer from "components/Common/Footer/Footer";

import "./CustomFields.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;

    showLoader?: (loaderVisibility: boolean) => void;
    showError?: (error: string) => void;
}

const CustomFields: React.FC<Props> = ({ showLoader, showError }) => {
    enableMapSet();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cancelTokenSource = useRequestCancellation();
    const params: Record<string, string> = useParams<{
        environmentId: string;
        tenantId: string;
    }>();

    const dispatch = useAppDispatch();

    const { environmentId, tenantId } = params;

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const customerType = useSelector((state: RootState) =>
        getTenantCustomerType(state)
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [initialized, setInitialized] = useState(false);

    const [targetTables, setTargetTables] = useImmer<
        ReadonlyMap<string, string>
    >(new Map());

    const [targetFields, setTargetFields] = useImmer<
        ReadonlyMap<string, string>
    >(new Map());

    const [lineTypes, setLineTypes] = useImmer<ReadonlyArray<ExcludedLineType>>(
        []
    );

    const [separators, setSeparators] =
        useImmer<ReadonlyArray<Separator> | null>(null);

    const [ttpMessageSourceFields, setTtpMessageSourceField] =
        useImmer<ReadonlyMap<string, TtpMessageSourceField> | null>(null);

    const [types, setTypes] = useImmer<ReadonlyArray<CustomFieldType> | null>(
        null
    );

    const [, setCustomFields] = useImmer<ReadonlyArray<CustomField>>([]);

    const [customFieldTableItems, setCustomFieldTableItems] = useImmer<
        TableItem<CustomField>[]
    >([]);

    const [updatedCustomFields, setUpdatedCustomFields] = useImmer<
        CustomField[]
    >([]);

    const [deletedCustomFields, setDeletedCustomFields] = useImmer<
        CustomField[]
    >([]);

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const erpType = getErpType(customerType);

    const title = template(t("tenantConfig.customFieldsPage.erpHeader"))({
        erpType: erpType,
    });

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([
            fetchErpCustomFields(),
            fetchDooapCustomFields(),
            fetchTargetTables(InvoiceType.Po),
            fetchTargetTables(InvoiceType.NonPo),
            fetchLineTypes(),
            fetchSeparators(),
            fetchTtpMessageSourceFields(InvoiceType.Po),
            fetchTtpMessageSourceFields(InvoiceType.NonPo),
            fetchTypes(),
        ])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchErpCustomFields = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadErpCustomFields(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setCustomFields(payload.data);
                setCustomFieldTableItems(
                    payload.data.map((field) => ({
                        ...field,
                        isValid: true,
                    }))
                );

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchDooapCustomFields = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadDooapCustomFields(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                const initialized = checkCustomFieldsInitialized(payload.data);

                setInitialized(initialized);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchTargetTables = async (
        invoiceType: InvoiceType
    ): Promise<string | null> => {
        try {
            const [payload, status] = await loadTargetTables(
                activeEnvironmentId,
                activeTenantId,
                invoiceType,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setTargetTables((draft) => {
                    const prefix = getInvoiceKeyPrefix(invoiceType);

                    payload.data.forEach((table) => {
                        draft.set(`${prefix}${table}`, table);
                    });
                });

                const promises = payload.data.map((table) =>
                    fetchTargetFields(table)
                );

                const value = await Promise.all(promises);

                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    return [...errors].join(". ");
                }

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchTargetFields = async (table: string): Promise<string | null> => {
        try {
            const [payload, status] = await loadTargetFields(
                activeEnvironmentId,
                activeTenantId,
                table,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setTargetFields((draft) => {
                    payload.data.forEach((item) => {
                        const prefix = getTableKeyPrefix(table);

                        draft.set(`${prefix}${item}`, item);
                    });
                });

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchLineTypes = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadLineTypes(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setLineTypes(payload.data);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchSeparators = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadSeparators(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setSeparators(payload.data);

                return null;
            }

            if (status === constants.statusCode.NotImplemented) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchTtpMessageSourceFields = async (
        invoiceType: InvoiceType
    ): Promise<string | null> => {
        try {
            const [payload, status] = await loadTtpMessageSourceFields(
                activeEnvironmentId,
                activeTenantId,
                invoiceType,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setTtpMessageSourceField((draft) => {
                    const prefix = getInvoiceKeyPrefix(invoiceType);

                    if (draft === null) {
                        const sourceFields = new Map<
                            string,
                            TtpMessageSourceField
                        >();

                        payload.data.forEach((item) => {
                            sourceFields.set(`${prefix}${item.field}`, item);
                        });

                        return sourceFields;
                    }

                    payload.data.forEach((item) => {
                        draft.set(`${prefix}${item.field}`, item);
                    });
                });

                return null;
            }

            if (status === constants.statusCode.NotImplemented) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchTypes = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadTypes(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setTypes(payload.data);

                return null;
            }

            if (status === constants.statusCode.NotImplemented) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const changeCustomFields = async () => {
        if (updatedCustomFields.length <= 0) {
            return null;
        }

        try {
            const [payload, status] = await updateCustomFields(
                activeEnvironmentId,
                activeTenantId,
                updatedCustomFields,
                cancelTokenSource
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const deleteCustomFields = async () => {
        if (deletedCustomFields.length <= 0) {
            return null;
        }

        try {
            const [payload, status] = await removeCustomFields(
                activeEnvironmentId,
                activeTenantId,
                deletedCustomFields,
                cancelTokenSource
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSubmit = () => {
        const valid = customFieldTableItems.every((item) => {
            const validated = validate(item);
            if (item.isValid !== validated.isValid) {
                handleChange({ ...item, ...validated });
            }

            return validated.isValid;
        });

        if (!valid) {
            return;
        }

        showLoader?.(true);

        const promises = [changeCustomFields(), deleteCustomFields()];

        Promise.all(promises).then((value) => {
            showLoader?.(false);
            dispatch(setIsUnsavedChangeAvailable(false));

            setUpdatedCustomFields([]);
            setDeletedCustomFields([]);

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = t("tenantConfig.customFieldsPage.successSent");

                fetchData();
            }

            toaster.push(
                <Message showIcon type={type}>
                    {message}
                </Message>,
                toastOptions
            );
        });
    };

    const handleChange = (data: TableItem<CustomField>) => {
        setUpdatedCustomFields((draft) => {
            const field = draft.find((f) => f.id === data.id);
            if (field) {
                Object.assign(field, data);
            } else {
                draft.push({ ...data });
            }
        });

        setCustomFieldTableItems((draft) => {
            const field = draft.find((f) => f.id === data.id);
            if (field) {
                const validated = validate(data);

                Object.assign(field, { ...data, ...validated });
            }
        });

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleAdd = (data: TableItem<CustomField>) => {
        setUpdatedCustomFields((draft) => {
            draft.push({ ...data });
        });

        setCustomFieldTableItems((draft) => {
            draft.push({ ...data });
        });

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleDelete = (data: TableItem<CustomField>) => {
        setUpdatedCustomFields((draft) => {
            const index = draft.findIndex((field) => field.id === data.id);
            if (index !== -1) {
                draft.splice(index, 1);
            }
        });

        setCustomFieldTableItems((draft) => {
            const index = draft.findIndex((field) => field.id === data.id);
            if (index !== -1) {
                draft.splice(index, 1);
            }
        });

        setDeletedCustomFields((draft) => {
            draft.push({ ...data });
        });

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleNavigateTo = () => {
        const url = buildUrl(
            constants.routePath.dooapCustomFields,
            Object.entries(params)
        );

        return navigate(url);
    };

    const validate = (rowData: TableItem<CustomField>) => {
        const result = model.check({
            targetTable: rowData.targetTable,
            targetField: rowData.targetField,
            separator: rowData.separator,
        });

        return {
            isValid:
                !result?.targetTable?.hasError &&
                !result?.targetField?.hasError,
            errors: {
                targetTable: result?.targetTable?.errorMessage ?? "",
                targetField: result?.targetField?.errorMessage ?? "",
            },
        };
    };

    useEffect(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, activeTenantId]);

    useRefreshPage(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (!activeEnvironmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!activeTenantId && activeEnvironmentId) {
            dispatch(fetchTenants(activeEnvironmentId, tenantId));
        }
    }, [activeEnvironmentId, dispatch]);

    const { StringType, NumberType } = Schema.Types;

    const model = Schema.Model({
        targetTable: StringType().isRequired(
            t("tenantConfig.customFieldsPage.validation.required")
        ),
        targetField: StringType().isRequired(
            t("tenantConfig.customFieldsPage.validation.required")
        ),
        separator: NumberType().isRequired(
            t("tenantConfig.customFieldsPage.validation.required")
        ),
    });

    return (
        dataFetched && (
            <Container className="custom-fields__erp__container">
                <Header>
                    <Heading level={1}>{title}</Heading>
                </Header>

                <Content className="custom-fields__erp__content">
                    {initialized ? (
                        <>
                            <Table
                                erpType={erpType}
                                invoiceType={InvoiceType.Po}
                                customFields={customFieldTableItems}
                                targetTables={targetTables}
                                targetFields={targetFields}
                                lineTypes={lineTypes}
                                separators={separators}
                                ttpMessageSourceFields={ttpMessageSourceFields}
                                types={types}
                                onRowChange={handleChange}
                                onRowAdded={handleAdd}
                                onRowDelete={handleDelete}
                            />

                            <Table
                                erpType={erpType}
                                invoiceType={InvoiceType.NonPo}
                                customFields={customFieldTableItems}
                                targetTables={targetTables}
                                targetFields={targetFields}
                                lineTypes={lineTypes}
                                separators={separators}
                                ttpMessageSourceFields={ttpMessageSourceFields}
                                types={types}
                                onRowChange={handleChange}
                                onRowAdded={handleAdd}
                                onRowDelete={handleDelete}
                            />

                            <Text
                                className="custom_fields_erp__footer"
                                style={{ marginTop: "1rem" }}
                            >
                                <sup className={"custom_fields__erp__sub"}>
                                    *
                                </sup>
                                {t(
                                    "tenantConfig.customFieldsPage.requiredFields"
                                )}
                            </Text>
                        </>
                    ) : (
                        <Message
                            style={{ margin: "1rem 0" }}
                            type="info"
                            showIcon
                            title={t("tenantConfig.emptyMessage.title.info")}
                        >
                            <>
                                <Button
                                    style={{
                                        paddingRight: "0",
                                        paddingLeft: "0",
                                    }}
                                    appearance="link"
                                    onClick={() => handleNavigateTo()}
                                >
                                    <Text color={"blue"} weight={"bold"}>
                                        {t(
                                            "tenantConfig.customFieldsPage.table.dooapLink"
                                        )}
                                    </Text>
                                </Button>
                                {template(
                                    t(
                                        "tenantConfig.customFieldsPage.table.dooapCustomFieldsRequiredTooltip"
                                    )
                                )({
                                    erpType: erpType,
                                })}
                            </>
                        </Message>
                    )}
                </Content>

                <RsuiteFooter>
                    <Footer onSubmit={() => handleSubmit()} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError)(CustomFields);

export default Component;
